import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { type ProjectDashboardSummaryCards } from '@gql/graphql';
import { type Maybe } from '@models/Maybe';
import { GET_PROJECT_DASHBOARD_SUMMARY_CARDS } from '@api/queries/dashboard';

interface SummaryCardsContextValue {
    data: Maybe<ProjectDashboardSummaryCards>;
    loading: boolean;
}

const SummaryCardsContext = createContext<SummaryCardsContextValue>({
    data: undefined,
    loading: false,
});

export function useSummaryCards(): SummaryCardsContextValue {
    return useContext(SummaryCardsContext);
}

export function SummaryCardsProvider({
    children,
}: {
    children: ReactNode;
}): ReactNode {
    const { pid } = useParams<{ pid: string }>() as { pid: string };

    const { data, loading } = useQuery(GET_PROJECT_DASHBOARD_SUMMARY_CARDS, {
        fetchPolicy: 'cache-and-network',
        variables: { pid },
        skip: !pid,
    });

    const value = useMemo(
        () => ({ data: data?.getProjectDashboardSummaryCards, loading }),
        [data, loading],
    );

    return (
        <SummaryCardsContext.Provider value={value}>
            {children}
        </SummaryCardsContext.Provider>
    );
}
